import {
  alpha,
  List,
  ListItemButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import React from "react";
import { GREY, VIOLET } from "@/config/mui/colorPalette";
import { TimeSlot } from "@/types/bookingTypes";
import { formatTimeTo12HourString } from "@/utils/formatTimeTo12HourString";

export default function TimeSlotGrid({
  selectedTimeSlot,
  onTimeSlotSelect,
  timeSlots,
  customizationPrimaryColor,
}: {
  selectedTimeSlot: TimeSlot | null;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  timeSlots: Array<TimeSlot>;
  customizationPrimaryColor?: string;
}) {
  const theme = useTheme();
  const formatTime = (timeString: string) => {
    // formats time from "2024-06-25T09:50:00-04:00" kind of string to "9:50 am"
    const [timePart] = timeString.split("T")[1].split("-");
    return formatTimeTo12HourString(timePart).toLowerCase();
  };

  const selectedBackgroundColor = customizationPrimaryColor
    ? alpha(customizationPrimaryColor, 0.04)
    : VIOLET[20];

  const hoverBackgroundColor = customizationPrimaryColor
    ? alpha(customizationPrimaryColor, 0.04)
    : VIOLET[20];

  // format for timeslot is "2024-06-25T09:50:00-04:00", use this fn to extract hour
  // to avoid using Date and system timezone
  const getHourFromTimeSlot = (timeSlot: TimeSlot): number => {
    return parseInt(timeSlot.startStr.split("T")[1].split(":")[0], 10);
  };

  // clasiffy time slots into morning, afternoon, evening
  const timeSlotsByPeriod = useMemo(
    () => [
      {
        title: "Morning",
        slots: timeSlots.filter((timeSlot) => {
          const hour = getHourFromTimeSlot(timeSlot);
          return hour < 12;
        }),
      },
      {
        title: "Afternoon",
        slots: timeSlots.filter((timeSlot) => {
          const hour = getHourFromTimeSlot(timeSlot);
          return hour >= 12 && hour < 18;
        }),
      },
      {
        title: "Evening",
        slots: timeSlots.filter((timeSlot) => {
          const hour = getHourFromTimeSlot(timeSlot);
          return hour >= 18;
        }),
      },
    ],
    [timeSlots]
  );

  const isSelectedTimeSlot = (timeSlot: TimeSlot) => {
    return selectedTimeSlot?.startStr === timeSlot.startStr;
  };

  return (
    <>
      {timeSlotsByPeriod.map(({ title, slots }) => {
        if (slots.length === 0) return null;
        return (
          <React.Fragment key={title}>
            <Typography variant="labelSmall">{title}</Typography>
            <List
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, 110px)",
                columnGap: 1.5,
                rowGap: 1.5,
                mb: 3,
              }}
            >
              {slots.map((timeSlot) => (
                <ListItemButton
                  key={timeSlot.startStr + timeSlot.endStr}
                  onClick={() => onTimeSlotSelect(timeSlot)}
                  selected={isSelectedTimeSlot(timeSlot)}
                  aria-label={`Select time slot at ${formatTime(timeSlot.startStr)}`}
                  sx={{
                    textAlign: "center",
                    borderColor: GREY[20],
                    color: "text.primary",
                    border: `1px solid ${GREY[20]}`,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: hoverBackgroundColor,
                    },
                    "&.Mui-selected": {
                      fontWeight: 700,

                      border: "1.5px solid",
                      borderColor: theme.palette.primary.main,
                      backgroundColor: selectedBackgroundColor,
                    },
                    height: "40px",
                    mb: 0,
                    justifyContent: "center",
                    "& .MuiTouchRipple-child": {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Typography
                    variant="paragraphSmall"
                    sx={{ fontWeight: "inherit" }}
                  >
                    {formatTime(timeSlot.startStr)}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </React.Fragment>
        );
      })}
    </>
  );
}
